// Wrapper de <img> que optimiza dinámicamente vía Cloudflare cuando está
// configurado. Cuando NO hay configuración, hace passthrough — la app sigue
// funcionando exactamente igual que antes.
//
// Modos soportados (orden de precedencia):
//   1) src ya es un imagedelivery.net/{hash}/{id}  → se le agrega variant.
//   2) VITE_CF_IMAGES_ENABLED === "true"           → se reescribe vía
//                                                    /cdn-cgi/image/<opts>/<src>
//      (requiere Image Resizing habilitado en la zona Cloudflare).
//   3) Default                                      → src tal cual.
//
// La idea es que migremos primero los <img> de eventos y branding a este
// componente, y cuando habilitemos CF Images el cambio es solo de env vars.

import type { ImgHTMLAttributes } from "react";

const CF_IMAGES_ENABLED =
  (import.meta.env.VITE_CF_IMAGES_ENABLED as string | undefined) === "true";

export type CldImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string | null | undefined;
  /** Ancho objetivo en CSS px. El CDN servirá el variant más cercano. */
  width?: number;
  /** Alto opcional para reservar layout (evita CLS). */
  height?: number;
  /** 1–100, default 80. */
  quality?: number;
  /** Fallback si src es null/empty. */
  fallbackSrc?: string;
};

function buildVariant(width?: number, quality = 80): string {
  const parts = [`format=auto`, `quality=${quality}`];
  if (width) parts.push(`width=${width}`);
  return parts.join(",");
}

function optimizeUrl(src: string, width?: number, quality = 80): string {
  // imagedelivery.net (Cloudflare Images nativo)
  if (src.startsWith("https://imagedelivery.net/")) {
    const parts = src.split("/");
    // https://imagedelivery.net/{hash}/{imageId}/{variant?}
    if (parts.length >= 5) {
      return `${parts.slice(0, 5).join("/")}/${buildVariant(width, quality)}`;
    }
    return src;
  }
  // /cdn-cgi/image/ resizing — requiere zona Cloudflare con Image Resizing on.
  if (CF_IMAGES_ENABLED && /^https?:\/\//.test(src)) {
    return `/cdn-cgi/image/${buildVariant(width, quality)}/${src}`;
  }
  return src;
}

export function CldImage({
  src,
  width,
  height,
  quality = 80,
  fallbackSrc,
  loading = "lazy",
  decoding = "async",
  ...rest
}: CldImageProps) {
  const finalSrc = src && src.length > 0 ? optimizeUrl(src, width, quality) : fallbackSrc;
  if (!finalSrc) return null;
  return (
    <img
      src={finalSrc}
      width={width}
      height={height}
      loading={loading}
      decoding={decoding}
      {...rest}
    />
  );
}
